/**
 * EDF / EDF+ header parsing.
 *
 * Layout (all fields are ASCII, left-justified, space-padded):
 *
 *   fixed header, 256 bytes
 *     0    8   version ('0', or 255 + 'BIOSEMI' for BDF)
 *     8   80   patient identification
 *    88   80   recording identification
 *   168    8   start date  dd.mm.yy
 *   176    8   start time  hh.mm.ss
 *   184    8   number of bytes in the header record
 *   192   44   reserved  ('EDF+C' / 'EDF+D' live here)
 *   236    8   number of data records (-1 if unknown)
 *   244    8   duration of a data record, in seconds (may be fractional)
 *   252    4   number of signals (ns)
 *
 *   signal header, ns * 256 bytes, stored FIELD-major rather than signal-major:
 *   all ns labels, then all ns transducer types, and so on.
 *     ns * 16   label
 *     ns * 80   transducer type
 *     ns *  8   physical dimension
 *     ns *  8   physical minimum
 *     ns *  8   physical maximum
 *     ns *  8   digital minimum
 *     ns *  8   digital maximum
 *     ns * 80   prefiltering
 *     ns *  8   number of samples in each data record
 *     ns * 32   reserved
 */

import { EdfError } from './errors.js';
import type { Diagnostic } from './errors.js';
import { counted, grouped, listed } from '../format/list.js';
import { plainSeconds } from '../format/number.js';
// The shell-quoting rule for a label, shared rather than repeated: this hint and
// `--channels`' own "did you mean" have to print the same command for the same label.
import { typeable } from '../convert/channels.js';
import { OptionError, describeValue } from '../convert/options.js';
import { decodeLatin1 } from './bytes.js';

/** Label the EDF+ spec reserves for the annotations channel. */
export const ANNOTATIONS_LABEL = 'EDF Annotations';
/** BDF+ uses its own spelling for the same channel. */
export const BDF_ANNOTATIONS_LABEL = 'BDF Annotations';

export const FIXED_HEADER_BYTES = 256;
export const SIGNAL_HEADER_BYTES = 256;

export interface EdfSignal {
  /** Position in the file, 0-based. Stable even when labels collide. */
  index: number;
  label: string;
  transducer: string;
  physicalDimension: string;
  physicalMin: number;
  physicalMax: number;
  digitalMin: number;
  digitalMax: number;
  prefiltering: string;
  samplesPerRecord: number;
  reserved: string;
  /** True for the EDF+ 'EDF Annotations' channel, which carries text, not signal. */
  isAnnotations: boolean;
  /** samplesPerRecord / recordDuration, in Hz. */
  samplingRate: number;
  /** Byte offset of this signal's samples within one data record. */
  byteOffsetInRecord: number;
}

export interface EdfHeader {
  version: string;
  patientId: string;
  recordingId: string;
  /** Raw 'dd.mm.yy' as written in the file. */
  startDateRaw: string;
  /** Raw 'hh.mm.ss' as written in the file. */
  startTimeRaw: string;
  /** Resolved start instant, or null when the file's date/time fields are unusable. */
  startDateTime: Date | null;
  /**
   * Header size in bytes, computed from the signal count rather than read from the field.
   *
   * Every data record offset is derived from this, so it has to be the one the layout
   * actually uses: 256 for the fixed header plus 256 per signal. A writer that fills the
   * field in carelessly is common enough to have its own warning, HEADER_BYTES_MISMATCH,
   * and trusting the field over the arithmetic would put every sample at the wrong offset.
   */
  headerBytes: number;
  /**
   * What the header's own length field says, which need not be the above.
   *
   * Exposed for the same reason `declaredRecordCount` is: the two disagreeing is a fact
   * about the file, and a caller checking how a recording was written should be able to see
   * what it claimed rather than only what was believed.
   */
  declaredHeaderBytes: number;
  reserved: string;
  isEdfPlus: boolean;
  /** True for BioSemi BDF/BDF+ files, whose samples are 3 bytes rather than 2. */
  isBdf: boolean;
  /** 'EDF+C' continuous, 'EDF+D' discontinuous, or null for plain EDF. */
  continuity: 'EDF+C' | 'EDF+D' | null;
  /** As declared in the header. -1 means "unknown", which the spec permits. */
  declaredRecordCount: number;
  recordDuration: number;
  signalCount: number;
  signals: EdfSignal[];
  bytesPerSample: number;
  recordBytes: number;
}

/** Everything derived by combining the header with the file's real size. */
export interface EdfHeaderInfo {
  header: EdfHeader;
  /** Record count implied by the actual file size — the one we trust for reading. */
  recordCount: number;
  /** Bytes after the last complete data record. */
  trailingBytes: number;
  diagnostics: Diagnostic[];
}

/**
 * Whether a spreadsheet reads this field as the start of a formula rather than as text.
 *
 * `=` and `@` unconditionally; every list of these characters names two more, and this had
 * `-` as an exception with the reason written out on the warnings page: "a lone `-` is a
 * real convention for no unit ... and neither is evaluated unless what follows it parses as a
 * formula". Which is the condition, and it was not being applied — nothing with a leading
 * minus was flagged at all. A channel labelled `-2+3` opens as a column headed `1`, and
 * `-HYPERLINK("http://...","EEG")` is a name the spreadsheet resolves, in silence.
 *
 * So the exception is what it says it is rather than the whole character. A lone `-` is left
 * as text by every spreadsheet and is not flagged; a field that is entirely a number reads as
 * that number, which is what the header says, and is not flagged either. Anything else after
 * the minus is arithmetic or a name.
 *
 * And `+` takes the same exception, which it did not. It is the same rule in the spreadsheet —
 * Lotus compatibility, which converts a leading `+` or `-` to a formula when what follows one
 * parses as a formula and leaves it as text when it does not — so the two signs cannot differ
 * here for a reason that comes from the sign. A channel labelled `+100` was warned about as
 * something a spreadsheet "reads as the start of a formula rather than as text", over a cell
 * that opens as 100, which is what the header says; `-100` beside it said nothing, and under
 * `--strict` the difference was an exit code. `+1+1` is still arithmetic and still flagged.
 */
export function startsFormula(text: string): boolean {
  if (/^[=@]/u.test(text)) return true;
  if (!/^[+-]./u.test(text)) return false;
  // `\.\d*` and not `\.\d+`, which is the spelling DECIMAL_FIELD and DECIMAL_DURATION both
  // use for the same question. A trailing point is a number with nothing after it — Excel
  // opens `+1.` as 1 — and one character of difference between three copies of one grammar
  // had this warning firing on a field the paragraph above exempts by name, and failing
  // --strict for it.
  return !/^[+-](?:\d+(?:\.\d*)?|\.\d+)(?:[eE][+-]?\d+)?$/u.test(text);
}

/**
 * A byte the terminal treats as an instruction rather than as text.
 *
 * C0 and C1, plus DEL. Tab is included deliberately: it is harmless to a terminal but it
 * makes a CSV column name that cannot be typed or matched reliably, which is the other half
 * of what this warning is for.
 */
function isControlCharacter(character: string): boolean {
  const code = character.codePointAt(0) as number;
  return code <= 0x1f || (code >= 0x7f && code <= 0x9f);
}

const dec = (buf: Uint8Array, start: number, len: number): string =>
  decodeLatin1(buf, start, start + len);

/** EDF fields are space-padded; trailing NULs also occur in files written by sloppy tools. */
const trimField = (s: string): string => s.replace(/[\0\s]+$/u, '').replace(/^\s+/u, '');

/**
 * How many signals the fixed header says there are, read exactly as `parseHeader` will.
 *
 * `EdfFile.open` needs this before it can know how much header to read, and it used to work
 * it out with its own `Number(...)` — which was NUL-tolerant but not comma-tolerant, unlike
 * every other numeric field here. A header written with a comma decimal separator, which
 * COMMA_DECIMAL exists to accept and which the documentation lists this field among, was
 * therefore never given its signal headers at all, and the file died on a message that
 * contradicted itself: "needs a 768-byte header, but the file is only 848 bytes".
 *
 * Sharing the parse is what keeps the two from disagreeing again about which files are
 * readable. Null means "not a usable count", and the caller reads no further header — the
 * real error then comes from `parseHeader`, which is the one place that decides.
 */
export function peekSignalCount(fixed: Uint8Array): number | null {
  const text = normaliseNumberField(dec(fixed, 252, 4)).text;
  if (!DECIMAL_FIELD.test(text)) return null;
  const count = Number(text);
  return Number.isInteger(count) && count > 0 ? count : null;
}

/**
 * What EDF allows a numeric field to look like, which is less than `Number()` allows.
 *
 * A sign, digits, an optional fractional part, an optional exponent — the spec's own grammar,
 * plus the exponent form the 8-character physical bounds need to reach a magnetometer's range
 * and which real headers use.
 *
 * `Number()` accepts a great deal more, and every one of those forms is a header this tool
 * would have read as a number nobody wrote. A physical maximum of `0x64` came out as 100: it
 * printed as `-100 to 100` in the channel table, went into channels.csv as `physical_max,100`,
 * and set the gain every sample on that channel was scaled by — a whole calibration invented
 * from four bytes that are not a decimal number, exit 0, no diagnostic. `0b1100100` and `0o144`
 * are the same hundred, and `0x02` in the signal-count field is a two-channel recording.
 *
 * The same mistake as `#0x2` reaching channel 2 through `--channels`, `--decimals 0o5` writing
 * five places and `--jobs 0x10` running sixteen, all of which have their own comments and their
 * own fixes. Those were values somebody typed. These are the fields every number in the output
 * is computed from, and the page describing them says "all fields are ASCII" and gives the
 * layout digit by digit.
 */
const DECIMAL_FIELD = /^[+-]?(?:\d+(?:\.\d*)?|\.\d+)(?:[eE][+-]?\d+)?$/u;

/** A numeric header field, trimmed and with a comma decimal separator turned into a dot. */
function normaliseNumberField(raw: string): { text: string; sawComma: boolean } {
  const text = trimField(raw);
  // Some writers emit a comma decimal separator despite the spec requiring '.'.
  if (text.includes(',') && !text.includes('.')) {
    return { text: text.replace(',', '.'), sawComma: true };
  }
  return { text, sawComma: false };
}

/**
 * What a header field that is not the number it must be usually means.
 *
 * `BAD_HEADER_FIELD` is raised four ways — an empty numeric field, one that does not parse, one
 * that must be whole and is not, and a signal declaring a negative sample count — and one of
 * the four carried this. The other three printed a bare sentence about a field and stopped,
 * which is the same gap `EdfFile.#UNREADABLE_HINT` was written to close on the read side: a
 * family of errors where one member says what the cause usually is and the rest do not.
 *
 * An empty field is if anything the plainer symptom of the two. A truncated download stops
 * mid-header and leaves the rest as spaces; a byte-shifted file reads a field off the wrong
 * offset and finds whitespace as often as it finds digits. The page has said all four mean
 * this since they were documented together.
 */
const BAD_FIELD_HINT = 'The file may be truncated, byte-shifted, or not an EDF file at all.';

function parseNumberField(
  raw: string,
  field: string,
  { integer = false, sawComma }: { integer?: boolean; sawComma?: { value: boolean } } = {},
): number {
  const normalised = normaliseNumberField(raw);
  const text = normalised.text;
  if (normalised.sawComma && sawComma) sawComma.value = true;
  if (text === '') {
    throw new EdfError('BAD_HEADER_FIELD', `Header field "${field}" is empty.`, BAD_FIELD_HINT);
  }
  const n = DECIMAL_FIELD.test(text) ? Number(text) : NaN;
  if (!Number.isFinite(n)) {
    throw new EdfError(
      'BAD_HEADER_FIELD',
      `Header field "${field}" is not a number (found ${JSON.stringify(text)}).`,
      BAD_FIELD_HINT,
    );
  }
  if (integer && !Number.isInteger(n)) {
    throw new EdfError(
      'BAD_HEADER_FIELD',
      `Header field "${field}" must be a whole number (found ${JSON.stringify(text)}).`,
      BAD_FIELD_HINT,
    );
  }
  return n;
}

/** Whether the start time names the sixtieth second. See LEAP_SECOND_START. */
function namesLeapSecond(timeRaw: string): boolean {
  const t = /^(\d{2})[.:\-](\d{2})[.:\-](\d{2})$/u.exec(trimField(timeRaw));
  return t !== null && Number(t[3]) === 60;
}

const MONTHS = ['JAN', 'FEB', 'MAR', 'APR', 'MAY', 'JUN', 'JUL', 'AUG', 'SEP', 'OCT', 'NOV', 'DEC'];

/**
 * The date an EDF+ recording ID states, which is the only place the file writes a full year.
 *
 * EDF+ requires the recording identification field to begin `Startdate dd-MMM-yyyy`, and
 * requires it to agree with the header's own date field. It is a four-digit year, so it says
 * something the eight-character date field physically cannot.
 */
function recordingIdStartdate(
  recordingId: string,
): { day: number; month: number; year: number } | null {
  const stated = /^Startdate\s+(\d{2})-([A-Za-z]{3})-(\d{4})(?:\s|$)/u.exec(recordingId.trim());
  if (!stated) return null;
  const month = MONTHS.indexOf((stated[2] as string).toUpperCase()) + 1;
  if (month === 0) return null;
  return { day: Number(stated[1]), month, year: Number(stated[3]) };
}

/**
 * EDF stores a two-digit year. The spec pins the century: 85-99 mean 1985-1999
 * and 00-84 mean 2000-2084. Files outside 1985-2084 cannot express their date.
 *
 * Which is why EDF+ writes it again in full, in the recording identification field, and why
 * that is used here when it is there. The rule alone reports a recording made in 1984 as 2084
 * and one made in 2085 as 1985 — a hundred years out, on a file that states the year plainly
 * four fields earlier. Taken only where the two agree about everything the header can express:
 * the same day, the same month, and a four-digit year ending in the two digits the header
 * wrote. A recording ID that contradicts the header is a different problem and is left to the
 * spec's rule, which is at least the one the format defines.
 */
function resolveStartDateTime(
  dateRaw: string,
  timeRaw: string,
  recordingId = '',
): Date | null {
  const d = /^(\d{2})[.\-/](\d{2})[.\-/](\d{2})$/u.exec(trimField(dateRaw));
  const t = /^(\d{2})[.:\-](\d{2})[.:\-](\d{2})$/u.exec(trimField(timeRaw));
  if (!d || !t) return null;

  const dd = Number(d[1]);
  const mm = Number(d[2]);
  const yy = Number(d[3]);
  const hh = Number(t[1]);
  const mi = Number(t[2]);
  const ss = Number(t[3]);

  if (mm < 1 || mm > 12 || dd < 1 || dd > 31 || hh > 23 || mi > 59 || ss > 60) return null;

  const stated = recordingIdStartdate(recordingId);
  const year =
    stated !== null && stated.day === dd && stated.month === mm && stated.year % 100 === yy
      ? stated.year
      : yy >= 85
        ? 1900 + yy
        : 2000 + yy;
  const date = new Date(Date.UTC(year, mm - 1, dd, hh, mi, Math.min(ss, 59)));
  // Reject dates that rolled over, e.g. 31.02.
  if (date.getUTCMonth() !== mm - 1 || date.getUTCDate() !== dd) return null;
  return date;
}

/**
 * Parse the fixed 256-byte header plus the per-signal header block.
 *
 * @param buf   At least FIXED_HEADER_BYTES + ns * SIGNAL_HEADER_BYTES bytes.
 * @param fileSize Total size of the file on disk, used to derive the real record count.
 */
export function parseHeader(buf: Uint8Array, fileSize: number): EdfHeaderInfo {
  /*
    The first argument, checked before anything is read out of it.

    The api page offers this function on its own, so it is reached by a caller holding bytes
    they read themselves — and it took whatever it was handed. A number or a plain object came
    back as Node's own `TypeError: bytes.subarray is not a function`, which names a method the
    caller never called; `undefined` as a property read on a local.

    Worse, a string got an answer *about the recording*:

        parseHeader('0       ...', 4_000_000)
        EdfError FILE_TOO_SMALL: An EDF header alone needs 256 bytes, but only 11 of this
        4,000,000-byte file reached the parser.

    A string has a `length`, so it reached the short-header branch below and was reported as a
    truncated file — the code a script branches on to quarantine a bad recording, raised over a
    recording nobody had read. That is the distinction `readRecords` draws in as many words:
    "an `OptionError`, because it is the call that is wrong and not the recording". The same
    check `decodeRecordAnnotations` makes of its own byte argument, for the reason given there.
  */
  /*
    A view of *bytes*, which `ArrayBuffer.isView` does not mean.

    It is true of every typed array and of `DataView`, and 0.8.74 read it as "these are
    bytes". Two views it lets through are not:

        parseHeader(new DataView(buffer), size)
        TypeError: bytes.subarray is not a function

    which is the exact failure that check was written to remove, quoted in its own changelog
    entry — a `DataView` has neither `subarray` nor `length`, so `buf.length < 256` is
    `undefined < 256`, false, and the guard below waves it through to the line that reads
    `buf[0]`. A `DataView` is what a caller reading a header by hand with `getUint8` holds.

        parseHeader(new Float64Array(100), 4_000_000)
        EdfError FILE_TOO_SMALL: ... only 100 of this 4,000,000-byte file reached the parser

    which is the other failure it was written to remove: `100` is the element count of a view
    holding 800 bytes, and the sentence blames the recording for it.

    `BYTES_PER_ELEMENT === 1` is the question actually being asked. It is true of `Uint8Array`,
    `Int8Array`, `Uint8ClampedArray` and the `Buffer` the reader hands this, and undefined on a
    `DataView`.
  */
  if (!ArrayBuffer.isView(buf) || (buf as { BYTES_PER_ELEMENT?: number }).BYTES_PER_ELEMENT !== 1) {
    /*
      A view is named rather than dumped.

      `describeValue` renders an object as its JSON, and a hundred-element `Float64Array` of
      header bytes comes back as a hundred doubles in the refusal — the caller's own data
      handed back, which is what 0.8.72 took out of the sentence next door. What locates the
      mistake is which view it is.
    */
    const takes =
      'parseHeader takes the bytes read from the file — a Uint8Array or a Buffer, one byte ' +
      'an element — and the size of the file they came out of.';
    throw new OptionError(
      ArrayBuffer.isView(buf)
        ? `buf is a ${(buf as object).constructor?.name ?? 'view'}, which is a view of an ` +
          `ArrayBuffer but not of bytes. ${takes}`
        : `buf must be the header block as bytes, got ${describeValue(buf)}. ${takes}`,
    );
  }

  /*
    The second argument, checked before anything is derived from it.

    Every count this function reports about the data comes out of `fileSize`, and nothing
    asked what it was. Omit it — which a JavaScript caller does by writing the call the way
    the one-argument functions beside it are written — and the arithmetic runs on `undefined`:

        parseHeader(bytes).recordCount            // NaN
        parseHeader(bytes).diagnostics[0].message
        // "The header declares 2 data records but the file contains NaN. Only the NaN
        //  records that are present can be converted."

    A header object claiming NaN records, a warning that says NaN out loud, and no error —
    and `recordCount * recordDuration`, which is how the duration is worked out everywhere
    this value goes, is NaN from there on. `null` was worse from 0.8.5, when the counts in
    these messages started being grouped: grouping it is a TypeError raised from inside the
    number formatter, naming nothing the caller wrote.

    The same `OptionError` `assertInputPath` raises for the first argument of `EdfFile.open`,
    for the same reason given there: it is the call that is wrong, not the recording.
  */
  /*
    And a whole number of them, which that check did not ask for.

    A file has an integer number of bytes; `fs.stat` reports one, and every count derived here
    divides by a record length in bytes. A fraction survived the division and came out in the
    warnings as though the recording had it:

        parseHeader(bytes, 848.5)   // "0.5 bytes after the last complete data record were
                                    //  ignored."
        parseHeader(bytes, 900.25)  // "The header declares 2 data records but the file
                                    //  contains 3." — and "12.25 bytes ... were ignored."

    Three quarters of a byte, reported as a fact about the file, under `TRAILING_BYTES` — the
    code a script matches to decide a recording was truncated. The paragraph above is about
    exactly this: "Every count this function reports about the data comes out of `fileSize`."
  */
  if (typeof fileSize !== 'number' || !Number.isInteger(fileSize) || fileSize < 0) {
    throw new OptionError(
      `fileSize must be the number of bytes in the file, got ${describeValue(fileSize)}.`,
    );
  }

  const diagnostics: Diagnostic[] = [];
  const sawComma = { value: false };

  if (buf.length < FIXED_HEADER_BYTES) {
    /*
      Which of the two is actually short — the same question the FILE_TOO_SMALL fifty lines
      down was fixed for, in the words its comment still uses: "The file size was quoted
      either way, so a caller that had read too little ... produced arithmetic that refuted
      itself." This is the raising that comment describes, and it went on doing it:

          parseHeader(new Uint8Array(100), 5000)
          File is 5,000 bytes; an EDF header alone needs at least 256.

      5,000 is not less than 256. A reader following that sentence looks for a truncation
      that is not there, and the number they are given to check against the file is the one
      that is fine. Reached from the library, where the caller holds both — `EdfFile.open`
      reads 256 bytes before it reads anything else, so the two agree by construction there.
    */
    const short = fileSize < FIXED_HEADER_BYTES;
    throw new EdfError(
      'FILE_TOO_SMALL',
      short
        ? `File is ${counted(fileSize, 'byte')}; an EDF header alone needs at least ${FIXED_HEADER_BYTES}.`
        : `An EDF header alone needs ${FIXED_HEADER_BYTES} bytes, but only ` +
          `${grouped(buf.length)} of this ${grouped(fileSize)}-byte file reached the parser.`,
      short
        ? 'A file this small is truncated, or is not an EDF or BDF recording at all: every one of ' +
          'them opens with a fixed 256-byte header.'
        : 'parseHeader takes the header block and the size of the file it came out of. This ' +
          'call was given fewer bytes than the header it was asked to read.',
    );
  }

  // BDF (BioSemi) marks itself with byte 255 followed by 'BIOSEMI', and stores
  // 3-byte samples instead of 2. Everything else about the layout is identical.
  const isBdf = buf[0] === 0xff && dec(buf, 1, 7) === 'BIOSEMI';
  const version = isBdf ? 'BIOSEMI' : trimField(dec(buf, 0, 8));

  const patientId = trimField(dec(buf, 8, 80));
  const recordingId = trimField(dec(buf, 88, 80));
  const startDateRaw = trimField(dec(buf, 168, 8));
  const startTimeRaw = trimField(dec(buf, 176, 8));
  const headerBytes = parseNumberField(dec(buf, 184, 8), 'number of header bytes', {
    integer: true,
    sawComma,
  });
  const reserved = trimField(dec(buf, 192, 44));
  const declaredRecordCount = parseNumberField(dec(buf, 236, 8), 'number of data records', {
    integer: true,
    sawComma,
  });
  const recordDuration = parseNumberField(dec(buf, 244, 8), 'duration of a data record', {
    sawComma,
  });
  const signalCount = parseNumberField(dec(buf, 252, 4), 'number of signals', {
    integer: true,
    sawComma,
  });

  if (signalCount <= 0) {
    throw new EdfError(
      'INVALID_SIGNAL_COUNT',
      `Header declares ${counted(signalCount, 'signal')}; expected at least 1.`,
      BAD_FIELD_HINT,
    );
  }
  if (!(recordDuration > 0)) {
    throw new EdfError(
      'INVALID_RECORD_DURATION',
      `Header declares a data record duration of ${plainSeconds(recordDuration)}s; expected a positive number.`,
      BAD_FIELD_HINT,
    );
  }

  const expectedHeaderBytes = FIXED_HEADER_BYTES + signalCount * SIGNAL_HEADER_BYTES;
  if (buf.length < expectedHeaderBytes) {
    throw new EdfError(
      'FILE_TOO_SMALL',
      /*
        Which of the two is actually short.

        The file size was quoted either way, so a caller that had read too little — the
        signal count parsed one way here and another way there — produced arithmetic that
        refuted itself: "needs a 768-byte header, but the file is only 848 bytes". A reader
        following that looks for a truncation that is not there.
      */
      `File declares ${counted(signalCount, 'signal')}, which needs a ${grouped(expectedHeaderBytes)}-byte header, ` +
        (fileSize < expectedHeaderBytes
          ? `but the file is only ${grouped(fileSize)} bytes.`
          : `but only ${grouped(buf.length)} bytes of it were handed to the parser.`),
      'Either the recording is truncated inside its own header, or the signal count was ' +
        'read from bytes that are not it. Both leave the channel descriptions unfinished.',
    );
  }
  if (headerBytes !== expectedHeaderBytes) {
    diagnostics.push({
      code: 'HEADER_BYTES_MISMATCH',
      severity: 'warning',
      message:
        `Header says it is ${grouped(headerBytes)} bytes, but ${counted(signalCount, 'signal')} ` +
        `${signalCount === 1 ? 'requires' : 'require'} ${grouped(expectedHeaderBytes)} bytes. ` +
        `Using the value computed from the signal count.`,
      hint:
        'Where the data starts is worked out from the signal count, not from this field, so ' +
        'nothing is read from the wrong offset. A writer that got this one wrong may have ' +
        'got others wrong too.',
    });
  }

  // Signal headers are field-major: all labels, then all transducers, and so on.
  const base = FIXED_HEADER_BYTES;
  const readField = (offsetUnits: number, width: number, i: number): string =>
    dec(buf, base + offsetUnits * signalCount + i * width, width);

  // EDF+ writes 'EDF+C'/'EDF+D' here; BDF+ writes 'BDF+C'/'BDF+D'. The two mean the
  // same thing, so both are normalised to a single continuity marker.
  const continuityTag = /^(?:EDF|BDF)\+([CD])/u.exec(reserved);
  const continuity: 'EDF+C' | 'EDF+D' | null =
    continuityTag === null ? null : continuityTag[1] === 'D' ? 'EDF+D' : 'EDF+C';

  const signals: EdfSignal[] = [];
  let byteOffsetInRecord = 0;
  const bytesPerSample = isBdf ? 3 : 2;
  const seenLabels = new Map<string, number[]>();
  const emptyLabels: number[] = [];

  for (let i = 0; i < signalCount; i++) {
    const label = trimField(readField(0, 16, i));
    const transducer = trimField(readField(16, 80, i));
    const physicalDimension = trimField(readField(96, 8, i));
    const physicalMin = parseNumberField(readField(104, 8, i), `physical minimum (signal ${i})`, {
      sawComma,
    });
    const physicalMax = parseNumberField(readField(112, 8, i), `physical maximum (signal ${i})`, {
      sawComma,
    });
    const digitalMin = parseNumberField(readField(120, 8, i), `digital minimum (signal ${i})`, {
      integer: true,
      sawComma,
    });
    const digitalMax = parseNumberField(readField(128, 8, i), `digital maximum (signal ${i})`, {
      integer: true,
      sawComma,
    });
    const prefiltering = trimField(readField(136, 80, i));
    const samplesPerRecord = parseNumberField(
      readField(216, 8, i),
      `samples per record (signal ${i})`,
      { integer: true, sawComma },
    );
    const sigReserved = trimField(readField(224, 32, i));

    if (samplesPerRecord < 0) {
      throw new EdfError(
        'BAD_HEADER_FIELD',
        `Signal ${i} ("${label}") declares ${samplesPerRecord} samples per record.`,
        BAD_FIELD_HINT,
      );
    }

    const isAnnotations = label === ANNOTATIONS_LABEL || label === BDF_ANNOTATIONS_LABEL;

    signals.push({
      index: i,
      label,
      transducer,
      physicalDimension,
      physicalMin,
      physicalMax,
      digitalMin,
      digitalMax,
      prefiltering,
      samplesPerRecord,
      reserved: sigReserved,
      isAnnotations,
      samplingRate: samplesPerRecord / recordDuration,
      byteOffsetInRecord,
    });
    byteOffsetInRecord += samplesPerRecord * bytesPerSample;

    if (!isAnnotations) {
      /*
        A label is free text out of the file, and it becomes a column name in signals.csv.

        `--info` has escaped control bytes since it was written, because an ANSI escape in a
        header can drive the reader's terminal — `\x1b[2J` clears the screen. The CSV had no
        such protection and needed none for correctness: quoting makes any byte safe for a
        parser, and this still passes the label through exactly as the file gives it, because
        losing what the header says is not an improvement.

        What was missing is the sentence saying so. A recording whose channel is labelled
        `\x1b[2Jgone` converted with no warning at all, and `cat signals.csv` then cleared
        the terminal — while a script referencing that column by name carried an invisible
        control character in it. NONPRINTABLE_LABEL has been declared and documented as
        reserved since 0.1; this is it doing its job.
      */
      /*
        Which of the two fields carries them, because the consequences are not the same.

        The message said "label or unit", and then said the bytes "will appear in the CSV
        column name" and that "the name cannot be typed" — both of which are about the label.
        A channel labelled plainly `ECG` in a unit of `u\x07V` got all of it: its column is
        `ECG`, `--channels ECG` selects it and exits 0, and the byte is in channels.csv's
        `unit` cell, which the warning never mentioned. Three sentences, none of them true of
        the file that raised it, on a warning whose whole purpose is to say where an invisible
        byte went.
      */
      /*
        All four free-text fields, not the two that were checked.

        `transducer` and `prefiltering` are free text out of the header exactly as the label
        and the unit are, and they land in channels.csv exactly as the unit does — so an ESC
        byte in a transducer field reached the CSV raw with nothing said, and `cat
        channels.csv` would drive the terminal. That is the hazard this warning exists for,
        two columns over. 0.5.71 made it name which field carries them; this is the rest of
        the fields it can name.
      */
      const fields = [
        ['label', label],
        ['unit', physicalDimension],
        ['transducer', transducer],
        ['prefiltering', prefiltering],
      ] as const;
      const affected = fields.filter(([, text]) => [...text].some(isControlCharacter));
      const control = affected.flatMap(([, text]) => [...text].filter(isControlCharacter));
      if (control.length > 0) {
        /*
          How many of them get named is the header's decision, and it was unbounded.

          Every other enumeration whose length the file controls goes through `listed`, which
          is the helper written for exactly this — "without letting the file decide how long
          the sentence gets". This one joined the whole set. Four free-text fields of 16, 8, 80
          and 80 bytes can carry 63 distinct control codes between them, and did:

              warning: Signal 0's label, unit and transducer contain 63 control characters
              (\x01, \x02, \x03, ... \x9e), which will appear in the CSV column name and in
              channels.csv exactly as the header has them.

          549 characters on one line, from a warning whose point is the short sentence around
          the list. The count is already in that sentence — "63 control characters" — so the
          tail the cut hides is the part the reader was told twice.
        */
        const shown = listed(
          [...new Set(control)].map(
            (c) => `\\x${(c.codePointAt(0) as number).toString(16).padStart(2, '0')}`,
          ),
        );
        const inLabel = affected.some(([name]) => name === 'label');
        // "label and unit", not "label, unit" — `listed` is for long enumerations that get
        // truncated, and this is a sentence with at most four items in it.
        const names = affected.map(([name]) => name);
        const named =
          names.length === 1
            ? (names[0] as string)
            : `${names.slice(0, -1).join(', ')} and ${names[names.length - 1] as string}`;
        // Where they land, which is the question the reader has. A label becomes a column
        // name in signals.csv; the other three are cells of channels.csv and nothing else.
        // Named down to the cell when there is one of them, because that is the answer to
        // "where did it go" — `channels.csv` alone leaves a reader scanning fourteen columns.
        /*
          And whether there is a channels.csv at all, which this took for granted.

          `--stdout` writes the signal table and nothing else — no channels.csv, no
          annotations.csv, no metadata.json. A control byte in a unit reaches no file at all
          in that mode, and this said it would be in a cell of one:

              $ edf2csv control-labels.edf --stdout | less
              warning: Signal 0's label and unit contain 2 control characters (\x1b), which
                       will appear in the CSV column name and in channels.csv's unit cell
                       exactly as the header has them.
                       ... Printing the CSV to a terminal may do more than print it.

          Which is the mode this warning matters most in: `--stdout` *is* printing the CSV to
          a terminal, and the hint two lines down says so. The header is parsed before any
          destination is chosen, so the sentence says what is true of both rather than
          guessing — the same reason the `DUPLICATE_LABEL` hint above names two layouts.
        */
        const cells = affected.filter(([name]) => name !== 'label').map(([name]) => name);
        const where =
          cells.length === 1
            ? `channels.csv's ${cells[0] as string} cell in any conversion that writes one`
            : 'the channels.csv of any conversion that writes one';
        /*
          "The CSV column name" is the wide layout's answer, given in both.

          A long signals.csv has three columns — time_s, channel, value — and a label is not
          one of them: it is a value in the `channel` column, once per row. So a control byte
          in a label reaches one cell of the header line under the default layout and every
          row of the table under `--layout long`, and the sentence naming where it went named
          only the first. Same fault 0.8.24 fixed for `DUPLICATE_LABEL`'s message and 0.8.43
          for its hint.

          The parser cannot know the layout, and unlike those two this sentence does not have
          room to name both — it already carries the field list, the byte list and where the
          cell fields land. "The channel's name" is what both layouts call it: the term the
          long layout's own warnings use, and what a column name is.
        */
        const lands =
          inLabel && cells.length > 0
            ? `which will appear as the channel's name in signals.csv and in ${where}`
            : inLabel
              ? "which will appear as the channel's name in signals.csv"
              : `which will appear in ${where}`;
        diagnostics.push({
          code: 'NONPRINTABLE_LABEL',
          severity: 'warning',
          message:
            `Signal ${i}'s ${named} ${affected.length === 1 ? 'contains' : 'contain'} ` +
            // A comma before the closing clause, which now has a clause of its own inside it.
            `${counted(control.length, 'control character')} (${shown}), ${lands}, exactly as the ` +
            `header has ${control.length === 1 ? 'it' : 'them'}.`,
          hint:
            /*
              Every branch has to print a command that works.

              The middle one quoted the label back, which is right until the label is empty:
              an unlabelled channel got `--channels ""`, and that exits 2 with "--channels was
              given but lists no channel names". A hint whose command fails is worse than no
              hint, and this warning's whole job is to say how to reach a channel whose header
              text you cannot type. `EMPTY_LABEL` already says the position is the only way in
              for such a channel; so does this now.

              A comma is the third way. `--channels` separates names with one, and splits on
              every occurrence, so a channel labelled `EEG Fpz-Cz, ref` cannot be selected by
              name at all: the quoted-back advice printed `--channels "EEG Fpz-Cz, ref"`, which
              exits 2 with `No channel named "EEG Fpz-Cz"` — a channel the file does not have,
              named after half of one it does. Commas in labels are ordinary, since EDF labels
              are free text, and the CSV header quotes them; only this one hint claimed
              something about them that isn't so.
            */
            /*
              And a fourth way, which this branch printed straight past.

              A shell expands `$`, a backtick and a backslash inside double quotes, so a
              channel labelled `EEG $ref` was answered with `--channels "EEG $ref"` — which
              arrives as `EEG ` and exits 2 on a channel the file does not have. A backtick
              opens a command substitution and the pasted line does not even finish. That is
              the failure 0.7.18 fixed for `--channels`' own suggestion, and `typeable` is the
              rule it fixed it with: double quotes where they survive, single quotes where
              they do not, null where nothing does. Asking it settles which branch this takes
              as well, so the two cannot disagree about one label again.
            */
            (typeable(label) === null
              ? `Address the channel by position with --channels "#${i}" rather than by name, ` +
                `since ${
                  inLabel
                    ? 'the name cannot be typed'
                    : label === ''
                      ? 'it has no label'
                      : 'a comma in the label would read as two names'
                }. `
              : `The column name is unaffected, so --channels ${typeable(label) as string} still selects it. `) +
            'Printing the CSV to a terminal may do more than print it.',
        });
      }

      /*
        Header text a spreadsheet will run rather than read.

        The same four free-text fields, asked a different question. `=`, `+` and `@` start a
        formula in Excel, LibreOffice and Sheets no matter which file the cell came from, and
        these fields land in a CSV header row and in channels.csv verbatim — so a channel
        labelled `=1+1` opens as a column headed 2, and `=HYPERLINK(...)` opens as a link
        nobody in the reading chain wrote. SECURITY.md already calls these fields
        attacker-controlled because they reach filenames; this is where they reach a program
        that executes text.

        Not `-`, which the same advice usually includes. A lone `-` is a real convention for
        "no unit" and appears in the fixtures, a leading `-` on a montage label is ordinary,
        and neither is executed unless what follows parses as a formula — so warning on it
        would fire on files that are fine, which is how a warning gets ignored.

        Said, not fixed. Prefixing the cell with a quote is the usual mitigation and would mean
        writing something the header does not say, which is the one thing this tool refuses to
        do; NONPRINTABLE_LABEL answers control bytes the same way.
      */
      const formulaic = fields.filter(([, text]) => startsFormula(text));
      if (formulaic.length > 0) {
        const names = formulaic.map(([name]) => name);
        const named =
          names.length === 1
            ? (names[0] as string)
            : `${names.slice(0, -1).join(', ')} and ${names[names.length - 1] as string}`;
        const shown = [...new Set(formulaic.map(([, text]) => text[0] as string))].join(', ');
        diagnostics.push({
          code: 'FORMULA_LABEL',
          severity: 'warning',
          message:
            `Signal ${i}'s ${named} ${formulaic.length === 1 ? 'starts' : 'start'} with ` +
            `${shown}, which Excel, LibreOffice and Google Sheets read as the start of a ` +
            `formula rather than as text.`,
          hint:
            'The text is written exactly as the header has it, so the cell is what the ' +
            'recording says. Open the CSV with pandas or R, or import it into the ' +
            'spreadsheet as text, if you do not want it evaluated.',
        });
      }

      if (label === '') {
        // Collected, not reported here: what this channel's column ends up called depends on
        // whether some later channel is literally labelled `signal_<i>`, and inside this loop
        // the later channels do not exist yet. See the pass below.
        emptyLabels.push(i);
      } else {
        // Collected rather than reported here: a label repeated five times should
        // produce one warning naming all five, not four near-identical pairs.
        const seen = seenLabels.get(label);
        if (seen) seen.push(i);
        else seenLabels.set(label, [i]);
      }

      if (samplesPerRecord === 0) {
        diagnostics.push({
          code: 'NO_SAMPLES',
          severity: 'warning',
          message: `Signal ${i} ("${label}") carries no samples at all (0 per data record).`,
          hint: 'It is described in channels.csv but left out of the converted data.',
        });
      }

      /*
        Too large to represent, and too small — the second was silent.

        The gain is the span divided by the digital range, and a span of 2e-320 over 65,535
        codes is 3e-325: below the smallest subnormal double, so it underflows to +0. The
        scaler's flat-range branch then handed every code the same physical value, and a
        channel of 65,536 distinct readings became one repeated number with nothing raised at
        all. One power of ten away, at 1e-319, the same file raises VALUE_RESOLUTION.

        Both are the same fact about the header — the span cannot be turned into a mapping —
        so both get this code, and both leave the cells empty rather than filling them with a
        value the header cannot justify.
      */
      const span = physicalMax - physicalMin;
      const underflowed = span !== 0 && span / (digitalMax - digitalMin) === 0;
      if (!Number.isFinite(span) || underflowed) {
        diagnostics.push({
          code: 'UNUSABLE_PHYSICAL_RANGE',
          severity: 'warning',
          message:
            `Signal ${i} ("${label}") declares a physical range from ${physicalMin} to ` +
            `${physicalMax}, whose span is too ${underflowed ? 'small' : 'large'} to ` +
            `represent, so its values cannot be scaled.`,
          hint: 'Its cells are left empty rather than filled with a value the header cannot justify.',
        });
      } else if (digitalMax === digitalMin) {
        diagnostics.push({
          code: 'DEGENERATE_DIGITAL_RANGE',
          severity: 'warning',
          message:
            `Signal ${i} ("${label}") has digital minimum equal to digital maximum ` +
            `(${digitalMin}), so its values cannot be scaled.`,
          hint: 'Its cells are left empty rather than filled with a value the header cannot justify.',
        });
      } else if (physicalMax === physicalMin) {
        diagnostics.push({
          code: 'DEGENERATE_PHYSICAL_RANGE',
          severity: 'warning',
          message:
            `Signal ${i} ("${label}") has physical minimum equal to physical maximum ` +
            `(${physicalMin}), so every sample converts to the same value.`,
          /*
            The one branch of the four that fills the cells, and the only one that said
            nothing about them.

            Its three neighbours all end "Its cells are left empty rather than filled with a
            value the header cannot justify", which is the opposite of what happens here: the
            mapping is defined — it has one point in it — so `physicalMin` is written into
            every cell, and a reader who had learned the family's answer from the branch above
            gets it backwards. A column of one repeated number is also the shape a genuinely
            flat recording takes, which is the confusion worth heading off.
          */
          hint:
            'Its cells carry that value rather than being left empty, since the mapping is ' +
            'defined — it just has one point in it. The distinct digital codes behind them ' +
            'are not recoverable from the CSV.',
        });
      } else if ((physicalMax - physicalMin) * (digitalMax - digitalMin) < 0) {
        /*
          Polarity is inverted when the gain is negative, and the gain is
          (physicalMax - physicalMin) / (digitalMax - digitalMin) — so it is the sign of the
          two spans together that matters, not the physical pair alone.

          Testing only `physicalMax < physicalMin` was wrong in both directions. A file with
          its DIGITAL bounds reversed is just as inverted and drew no warning at all, handing
          back sign-flipped EEG with nothing to indicate it. A file with BOTH pairs reversed
          has a positive gain and is not inverted, yet was warned about — a message that was
          simply untrue of that recording.
        */
        const reversed =
          physicalMax < physicalMin
            ? `physical minimum ${physicalMin} above physical maximum ${physicalMax}`
            : `digital minimum ${digitalMin} above digital maximum ${digitalMax}`;
        diagnostics.push({
          code: 'INVERTED_PHYSICAL_RANGE',
          severity: 'warning',
          message: `Signal ${i} ("${label}") declares ${reversed}, which inverts its polarity.`,
          hint: 'The values are converted exactly as the header specifies, inversion included.',
        });
      }
    }
  }

  /*
    What an unlabelled channel is actually called, which the message used to guess.

    A channel with no label takes `signal_<index>` — unless another channel is literally
    labelled that, which EDF permits, since labels are free text and nothing enforces anything
    about them. Then both collide and both are suffixed. The warning said "It will appear as
    "signal_0"" while the file's header read `time_s,signal_0_ch0,signal_0_ch1`: the one
    sentence the run printed named a column that exists in neither signals.csv nor
    channels.csv.

    The other half was silent. The channel that genuinely carries the label `signal_0` lost
    its own column name to a collision with a synthesised one, and nothing said so —
    DUPLICATE_LABEL did not fire, because the two labels are not the same label. Both halves
    are one sentence here, because they are one event.

    No specific suffixed name is quoted. The suffix rule has a second pass for names that are
    still shared afterwards, and a message that hard-coded `_ch<index>` would be guessing again
    in exactly the way this is fixing.
  */
  for (const index of emptyLabels) {
    const taken = seenLabels.get(`signal_${index}`);
    diagnostics.push({
      code: 'EMPTY_LABEL',
      severity: 'warning',
      message:
        taken === undefined
          ? `Signal ${index} has no label. It will appear as "signal_${index}".`
          : `Signal ${index} has no label, so it takes the name "signal_${index}" — which ` +
            `${taken.length === 1 ? 'signal' : 'signals'} ${listed(taken.map(String))} already ` +
            `${taken.length === 1 ? 'carries' : 'carry'} as a label, so both columns are ` +
            `suffixed with their position instead.`,
      /*
        The position, not the word "position".

        A hint that offers a command has to offer one that can be typed — 0.7.18 and 0.7.19
        went through the others making sure each one could be pasted, and NONPRINTABLE_LABEL
        two hundred lines up writes `--channels "#${i}"` with the number in it. This one said
        `--channels "#<position>"` while the sentence above it began "Signal 0", so the
        reader was asked to carry a number across from the message and put it in themselves.
      */
      hint:
        'The name is built from the position rather than read from the header, so it moves ' +
        `if the file's channels do. Select the channel as --channels "#${index}" to say ` +
        'which one you mean without depending on it.',
    });
  }

  /*
    A timestamp that is not one.

    EDF gives the start date and time eight characters each, and nothing stops a writer
    putting `32.13.99` and `25.61.61` there. `--info` has always echoed the raw fields with
    "(unparseable)" beside them, but nothing was raised: the conversion exited 0, `--strict`
    passed, and metadata.json recorded `start_datetime_local: null` with no note against it.

    Every other unusable header field reports itself — a degenerate digital range, a physical
    span that cannot be represented, a comma decimal separator, a header whose declared size
    disagrees with its signal count. This was the one that did not, and it is the field
    output-files points at for turning `time_s` into an absolute instant.
  */
  /*
    The sixtieth second, which is a second UTC has and a calendar date does not.

    `resolveStartDateTime` admits `ss === 60` on purpose — a recorder synchronised to UTC
    through a leap second writes `23.59.60`, and refusing it would throw away a date that is
    otherwise perfectly good over one second. What it then does is `Math.min(ss, 59)`, because
    `Date.UTC(..., 60)` rolls over into the next minute and would move the instant the other
    way, by fifty-nine seconds more.

    Keeping the nearest instant is the right answer. Keeping it in silence was not: `--info`
    printed `Recorded 2020-01-01 23:59:59` and metadata.json recorded the same, for a header
    that says `23.59.60`, with `--strict` exiting 0. Every other header field this tool cannot
    represent exactly says so — a comma decimal separator, a physical span that overflows, a
    record count that disagrees with the file — and this is the field `time_s` is documented as
    being added to.
  */
  if (
    namesLeapSecond(startTimeRaw) &&
    resolveStartDateTime(startDateRaw, startTimeRaw, recordingId) !== null
  ) {
    diagnostics.push({
      code: 'LEAP_SECOND_START',
      severity: 'warning',
      message:
        `The header's start time ("${startTimeRaw}") names the sixtieth second of a minute, ` +
        `which no calendar date has.`,
      hint:
        'It is recorded as the fifty-ninth second, one second earlier, since that is the ' +
        'nearest instant a date can hold. time_s is unaffected — it counts from the start of ' +
        'the recording either way.',
    });
  }

  /*
    Two dates in one header, disagreeing.

    EDF+ requires the recording identification field's `Startdate` to be the header's start
    date. Where it is, its four digits settle the century — see resolveStartDateTime. Where it
    is not, one of the two is wrong and there is no way to tell which, so the date field is
    used, being the one the format defines. That was done in silence, on a header that plainly
    contradicts itself:

        Recorded   2002-03-02 22:15:00
        Recording  Startdate 05-MAR-2002 PSG-1234/2002 NN Telemetry03

    The same shape as a record count that disagrees with the file, or a declared header size
    that disagrees with the signal count, both of which have said so for versions.
  */
  const statedDate = recordingIdStartdate(recordingId);
  const headerDate = /^(\d{2})[.\-/](\d{2})[.\-/](\d{2})$/u.exec(trimField(startDateRaw));
  if (statedDate !== null && headerDate !== null) {
    const day = Number(headerDate[1]);
    const month = Number(headerDate[2]);
    const yy = Number(headerDate[3]);
    if (statedDate.day !== day || statedDate.month !== month || statedDate.year % 100 !== yy) {
      diagnostics.push({
        code: 'START_DATE_MISMATCH',
        severity: 'warning',
        message:
          `The header's start date ("${startDateRaw}") and the date its recording ` +
          `identification states ("${String(statedDate.day).padStart(2, '0')}-` +
          `${MONTHS[statedDate.month - 1] as string}-${statedDate.year}") are different ` +
          `dates, which EDF+ does not permit.`,
        hint:
          'The start date field is used, since that is the one the format defines. Which of ' +
          'the two is right is not knowable from the file, so start_datetime_local may name ' +
          'the wrong day.',
      });
    }
  }

  if (resolveStartDateTime(startDateRaw, startTimeRaw, recordingId) === null) {
    diagnostics.push({
      code: 'START_TIME_UNREADABLE',
      severity: 'warning',
      message:
        `The header's start date and time ("${startDateRaw}" and ` +
        `"${startTimeRaw}") are not a date and a time, so the recording has ` +
        `no start instant.`,
      hint:
        'time_s is unaffected — it counts from the start of the recording either way. What ' +
        'cannot be done is turning it into a wall-clock instant, and metadata.json records ' +
        'start_datetime_local as null.',
    });
  }

  for (const [label, indices] of seenLabels) {
    if (indices.length < 2) continue;
    diagnostics.push({
      code: 'DUPLICATE_LABEL',
      severity: 'warning',
      /*
        Cut by the function that cuts every other list in a sentence here.

        `join` names all of them, however many there are, and a header may declare as many
        channels as it likes under one label: a 200-channel montage all labelled `T8-P8` — which
        is exactly the kind of file this warning is for, since CHB-MIT ships two of them —
        produced a single 1,100-character line of positions with the sentence that mattered at
        the front of it. `listed` shows eight and counts the rest, which is what the rate
        warning, the leftover-file warning, the channel-position lists in `--channels` and the
        `EMPTY_LABEL` message one loop up all already do. This was the last `join` of a
        file-controlled list left in a diagnostic.

        Two positions render identically either way, so the ordinary duplicate reads as it
        always has.
      */
      /*
        Positions written `#N`, which is how a position is written everywhere it is meant to be
        typed: `--channels "#0"`, "This file has signal channels at #0, #1, #2", and this
        warning's own namesake from channel selection — "(positions #0, #1); all of them were
        selected". This one said "(positions 0, 1)", two paragraphs above the page that tells
        the reader to "address it by position with #N". The number is the same; the form that
        works is not.
      */
      message:
        `${counted(indices.length, 'signal')} share the label "${label}" ` +
        `(positions ${listed(indices.map((i) => `#${i}`))}).`,
      /*
        A sentence about the wide layout, printed in both — the same fault 0.8.24 fixed for
        the other raiser of this code, two lines further down the same run.

        A long signals.csv has three columns, `time_s`, `channel` and `value`, and none of
        them comes from a label: a channel appears there as a value in the `channel` column.
        `label-suffix-collision.edf --layout long` printed both halves back to back:

            warning: 2 signals share the label "T8" (positions #0, #1).
                     Their columns are suffixed with the signal number so they stay
                     distinguishable.
            warning: Signal 2 is labelled "T8_ch0", ... so it is named "T8_ch0_ch2" in the
                     channel column.
                     Channel names are unique; look this channel up in channels.csv by its
                     signal_index.

        The plan raises the second and knows the layout. The header raises this one and
        cannot: it is parsed before any conversion is planned, and `EdfFile.open` hands these
        diagnostics to callers who have asked for no layout at all. So the sentence names
        both, rather than guessing which one is being written.
      */
      hint:
        'Their names are suffixed with the signal number so they stay distinguishable: a ' +
        'column name each in the wide layout, and a distinct value in the channel column ' +
        'under --layout long.',
    });
  }

  const recordBytes = byteOffsetInRecord;
  if (recordBytes <= 0) {
    throw new EdfError(
      'NO_SAMPLES',
      'No signal in this file carries any samples (every channel declares 0 samples per record).',
      'The header describes the channels and nothing has data behind them, so there is no ' +
        'record to read. A header written before recording began, or copied from another ' +
        'file, produces this.',
    );
  }

  if (sawComma.value) {
    diagnostics.push({
      code: 'COMMA_DECIMAL',
      severity: 'warning',
      message: 'Some header numbers use a comma decimal separator, which the EDF spec does not allow.',
      hint: 'They were read as decimal points. Check the values in the channel table.',
    });
  }

  const dataBytes = fileSize - expectedHeaderBytes;
  if (dataBytes < 0) {
    // With the two figures, like the other two FILE_TOO_SMALL messages and unlike this one,
    // which named neither — the same thing the NO_DATA_RECORDS message below was fixed for:
    // "the message carried no figures at all, so nothing in it could be checked against the
    // file". Reached by a caller handing `parseHeader` a header and a smaller size than the
    // one it came out of, which is a mismatch worth being able to see.
    throw new EdfError(
      'FILE_TOO_SMALL',
      `File is ${counted(fileSize, 'byte')}, which is less than the ${grouped(expectedHeaderBytes)} ` +
        `its own header occupies.`,
      'The header and the size came from different reads: parseHeader was handed a byte ' +
        'count smaller than the header block it was given.',
    );
  }
  const recordCount = Math.floor(dataBytes / recordBytes);
  const trailingBytes = dataBytes - recordCount * recordBytes;

  if (recordCount === 0) {
    /*
      Which of the two, and with the numbers.

      "The recording was probably interrupted before any data was written" is right about an
      empty file and wrong about the other way to get here: a header declaring records larger
      than the data present. A 606 KB file holding 589 KB of samples — 60% of one record, more
      than half a million readings — was told no data was written, and the message carried no
      figures at all, so nothing in it could be checked against the file. The declared record
      size is the thing to look at, and it was the one thing not said.

      Still an error either way. A record is the unit the format is addressed in, and there is
      nothing smaller to convert.
    */
    const empty = dataBytes === 0;
    throw new EdfError(
      'NO_DATA_RECORDS',
      empty
        ? 'The file contains a header and no data at all.'
        : `The file contains ${counted(dataBytes, 'byte')} of data, which is less than the ` +
          `${grouped(recordBytes)} its header says one data record takes.`,
      empty
        ? 'The recording was probably interrupted before any data was written.'
        : 'Either the recording was cut short part way through its first record, or the ' +
          'header describes records larger than the ones actually written. Check the ' +
          'samples-per-record fields against the file size.',
    );
  }

  if (declaredRecordCount === -1) {
    diagnostics.push({
      code: 'RECORD_COUNT_UNKNOWN',
      severity: 'warning',
      message:
        `The header does not say how many data records the file has (-1), which the spec allows ` +
        `for recordings still in progress. Using the ${counted(recordCount, 'record')} the file actually contains.`,
      hint:
        'That count comes from the size of the file, so a recording still being written ' +
        'converts as much of it as was on disk when this run read it.',
    });
  } else if (declaredRecordCount !== recordCount) {
    diagnostics.push({
      code: 'RECORD_COUNT_MISMATCH',
      severity: 'warning',
      // Both counts through `counted`, not only the second. A header declaring one record
      // over a file holding three read "declares 1 data records" — the slip the sentence
      // beside it has been holding to since it was written.
      message:
        `The header declares ${counted(declaredRecordCount, 'data record')} but the file ` +
        `contains ${grouped(recordCount)}. Only the ${counted(recordCount, 'record')} that ` +
        `${recordCount === 1 ? 'is' : 'are'} present can be converted.`,
      hint:
        declaredRecordCount > recordCount
          ? 'The recording looks truncated. It may have been cut short or copied incompletely.'
          : 'The file is longer than its header claims.',
    });
  }

  if (trailingBytes > 0) {
    diagnostics.push({
      code: 'TRAILING_BYTES',
      severity: 'warning',
      message: `${counted(trailingBytes, 'byte')} after the last complete data record ${trailingBytes === 1 ? 'was' : 'were'} ignored.`,
      hint:
        'A record is the unit this format is addressed in, and a partial one says nothing ' +
        'about which samples it holds or when they were taken. Every complete record is ' +
        'converted.',
    });
  }

  const isEdfPlus = continuity !== null;
  if (continuity === 'EDF+D') {
    diagnostics.push({
      code: 'DISCONTINUOUS',
      severity: 'warning',
      /*
        What the marker says, which is all this function has.

        The sentence asserted a fact about the records — "its data records are not contiguous
        in time" — from a header field, one record's worth of which this has not read. An
        EDF+D file whose records happen to sit end to end is unusual and perfectly legal, and
        the rest of the report already knows: `--info` prints the `Time span` line only when
        the span and the duration differ, so on such a file it stays silent and this warning
        three lines below it says the opposite.

        The marker is what is worth reporting, and it is what the continuous twin of this
        warning reports — "This file is marked continuous (EDF+C), but 1 of its 3 data records
        says it starts somewhere other than where continuity puts it": the field first, the
        evidence after, raised where the evidence is. `withTimingPromiseKept` already withdraws
        this warning's hint for the same reason, in the same place, and left the sentence above
        it making a claim of its own.
      */
      message:
        `This recording is marked discontinuous (${isBdf ? 'BDF+D' : 'EDF+D'}): its data ` +
        `records need not be contiguous in time.`,
      hint: 'Each row carries its true recording time, so gaps stay visible instead of being closed.',
    });
  }

  const dataSignals = signals.filter((s) => !s.isAnnotations);
  if (dataSignals.length === 0) {
    diagnostics.push({
      code: 'NO_SIGNAL_CHANNELS',
      severity: 'warning',
      /*
        The marker as the file spells it, which is what the DISCONTINUOUS warning ten lines up
        already does and what `describeFormat` was fixed for.

        A BDF+ recording holding nothing but its `BDF Annotations` channel — a label this
        parser recognises by name, beside `EDF Annotations`, and has since annotations were
        read at all — was told it "contains only EDF+ annotations", naming a channel it does
        not carry and a format it is not. `--info` two lines above says `BDF+ (continuous)`
        and the channel table names the channel; this warning was the one line disagreeing
        with both.
      */
      message: `This file has no signal channels; it contains only ${isBdf ? 'BDF+' : 'EDF+'} annotations.`,
      /*
        What the file is, not what the run will write. The warning underneath it — raised by
        the conversion rather than by the parser — already answers the second question, in
        those words: "annotations.csv holds whatever events it carries. channels.csv lists
        signal channels, so it has none to list." Two consecutive hints saying that would be
        worse than the one that was missing.
      */
      hint:
        `An ${isBdf ? 'BDF+' : 'EDF+'} file of events and no signal is an ordinary thing: a ` +
        'scoring file distributed beside the recording it annotates has exactly this shape.',
    });
  }

  // A channel declaring zero samples per record has no sampling rate to speak of — it is
  // reported separately as NO_SAMPLES and no file is written for it. Counting its nominal
  // 0 Hz as a rate made a single-rate recording warn that it used "2 different sampling
  // rates (4 Hz, 0 Hz)" and claim it was splitting output it never split.
  const rates = new Set(dataSignals.filter((s) => s.samplesPerRecord > 0).map((s) => s.samplingRate));
  if (rates.size > 1) {
    diagnostics.push({
      code: 'MIXED_SAMPLING_RATES',
      severity: 'warning',
      message:
        `Channels use ${counted(rates.size, 'different sampling rate')} ` +
        `(${listed(formatRates([...rates].sort((a, b) => b - a)).map((r) => `${r} Hz`))}).`,
      hint: 'They are written to one file per rate so no channel is resampled.',
    });
  }

  return {
    header: {
      version,
      patientId,
      recordingId,
      startDateRaw,
      startTimeRaw,
      startDateTime: resolveStartDateTime(startDateRaw, startTimeRaw, recordingId),
      headerBytes: expectedHeaderBytes,
      declaredHeaderBytes: headerBytes,
      reserved,
      isEdfPlus,
      isBdf,
      continuity,
      declaredRecordCount,
      recordDuration,
      signalCount,
      signals,
      bytesPerSample,
      recordBytes,
    },
    recordCount,
    trailingBytes,
    diagnostics,
  };
}

/**
 * The recording start as a zone-less wall clock, "YYYY-MM-DDTHH:MM:SS".
 *
 * EDF stores the start time as local wall-clock digits with no timezone anywhere in
 * the format. `startDateTime` is built with Date.UTC purely so those digits survive a
 * round trip unshifted, which makes it a carrier for the wall clock rather than a
 * real instant. Serialising it with `toISOString()` would append a Z and assert UTC,
 * and any reader converting to local time would then shift the recording by their own
 * offset: 13:43:04 in the file becomes 08:43:04 in New York. The Z is omitted because
 * the file genuinely does not say which zone it meant.
 */
export function formatWallClock(date: Date | null): string | null {
  /*
    `null` is the answer for a recording with no start instant. It was also the answer for
    every other falsy value, because this line used to read `if (!date) return null` and ran
    before the check below.

        formatWallClock(0)          // null
        formatWallClock(new Date(0))  // "1970-01-01T00:00:00"

    A millisecond timestamp is the obvious thing to hold beside a `Date` and the obvious thing
    to pass by mistake, and `0` is the epoch — a real instant, answered with the sentence this
    tool uses for a recording whose header has no readable date. `''`, `false` and `NaN` went
    the same way, while a truthy non-Date was refused properly. 0.8.63 gave this function its
    check; the short-circuit above it meant half the values never reached it.
  */
  if (date === null) return null;
  if (!(date instanceof Date)) {
    throw new OptionError(`date must be a Date or null, got ${describeValue(date)}.`);
  }
  /*
    And an unparseable one, which is a date this cannot state rather than a bad argument.

    `new Date('the third').toISOString()` is a `RangeError: Invalid time value` out of the
    middle of this function. `null` is what this returns for a recording with no start
    instant, and that is what an invalid Date is: `START_TIME_UNREADABLE` says the same thing
    about the same fact, and metadata.json writes `start_datetime_local: null` for it.
  */
  if (Number.isNaN(date.getTime())) return null;
  return date.toISOString().slice(0, 19);
}

/**
 * The recording's format, as `--info`, `metadata.json` and `--json` all name it.
 *
 * `"EDF"`, `"BDF"`, or one of `"EDF+ (continuous)"`, `"EDF+ (discontinuous)"`,
 * `"BDF+ (continuous)"`, `"BDF+ (discontinuous)"`. A BDF+ file reports its own spelling even
 * though `continuity` normalises the marker to the `EDF+` form.
 *
 * This said `EDF+ (EDF+D)` and `BDF+ (EDF+C)`, which are not strings it can return — the
 * parenthetical is the word, not the marker. It is a one-line doc comment on a public export,
 * so it is what a TypeScript consumer's editor shows and what `dist/edf/header.d.ts` ships,
 * and the value it describes is `recording.format` in every metadata.json this tool writes.
 * A consumer branching on the tooltip's spelling never matches. Every documentation page had
 * it right; this was the only place that did not.
 */
export function describeFormat(header: EdfHeader): string {
  /*
    The argument, which this read two booleans off and asked no questions about.

    Both reads are truthiness tests, so anything without them answers `'EDF'` — including the
    `EdfFile` whose `.header` this wants, which is one property away and is how the api page
    writes every other call. `describeFormat(file)` on a discontinuous BDF+ recording returned
    `'EDF'`: not an error, not a fallback, a confident wrong answer about the file in hand.

    The same check `makeScaler` got in 0.8.61, for the same reason — an exported function that
    reads fields off an object cannot tell a header from anything else that lacks them.
  */
  if (
    typeof header !== 'object' ||
    header === null ||
    typeof header.isBdf !== 'boolean' ||
    typeof header.isEdfPlus !== 'boolean'
  ) {
    throw new OptionError(`header must be a parsed EDF header, got ${describeValue(header)}.`);
  }
  const base = header.isBdf ? 'BDF' : 'EDF';
  if (!header.isEdfPlus) return base;
  /*
    The third field this reads, which the check above does not ask about.

    It decides the word in the parentheses, and the line below treats anything that is not
    `'EDF+D'` as continuous — missing included. So an object carrying the two booleans and
    nothing else came back `"EDF+ (continuous)"`: a confident wrong answer about a file,
    which is what that check was added to stop, for the one field it left out.

    A parsed header cannot be in this state: `isEdfPlus` *is* `continuity !== null`, so when
    the first is true the second is one of the two markers. Anything else reaching here is
    the argument being the wrong object again.
  */
  if (header.continuity !== 'EDF+C' && header.continuity !== 'EDF+D') {
    throw new OptionError(
      `header.continuity must be "EDF+C" or "EDF+D" on an EDF+ header, got ` +
        `${describeValue(header.continuity)}. It is what the word in the parentheses reports.`,
    );
  }
  return `${base}+ (${header.continuity === 'EDF+D' ? 'discontinuous' : 'continuous'})`;
}

/** Render a sampling rate without trailing noise: 256, 0.5, 12.5. */
export function formatRate(hz: number): string {
  // A number, like the neighbours. `formatRate(NaN)` came back as the string "NaN", and
  // `rateSlug` turned it into "NaNhz" — a file name this tool cannot write, handed to a
  // caller the doc comment below describes as "reaching for the exported slug function to
  // predict a filename". A rate that is not a number is not a rate.
  /*
    And a negative one, which 0.8.63 left in for the same reason it took NaN out.
    `rateSlug(-1)` answered `-1hz`, so `signals_-1hz.csv` — another name this tool cannot
    write, from the same caller asking the same question.

    Nor can a recording produce one to ask about: a rate is a channel's samples per record
    over the record duration, the parser refuses a record duration that is not positive, and
    it refuses a negative sample count outright — "Signal 0 declares -1 samples per record".
    Zero and Infinity are left alone, because both are rates a header really can state.
  */
  if (typeof hz !== 'number' || Number.isNaN(hz) || hz < 0) {
    throw new OptionError(`hz must be a sampling rate in hertz, got ${describeValue(hz)}.`);
  }
  if (Number.isInteger(hz)) {
    /*
      The same six decimals, in the notation `toFixed` cannot reach.

      Every double past 2^53 is an integer, so this branch takes every large rate — and
      `String` switches to exponent form at 1e21 and carries the full seventeen digits with
      it. Four samples in a record of 1e-300s is 3.9999999999999996e+300, which is exactly the
      float noise the rounding below exists to remove, printed in a `RATE` column of otherwise
      plain numbers and pasted into an output filename by `rateSlug`. Below 1e21 `String` is
      already exact and is left alone, so an integer rate a file can really state — 2^53
      samples in a second — is not rounded to something it is not.
    */
    const text = String(hz);
    return text.includes('e') ? String(Number(hz.toExponential(6))) : text;
  }
  const rounded = Number(hz.toFixed(6));
  // A rate below 5e-7 rounds away to "0", which reads as "this channel has no sampling
  // rate" and made the mixed-rate warning contradict itself: it announced two different
  // rates and then printed both as "0 Hz". Exponent form keeps a real rate legible, and
  // keeps distinct rates distinct in the channel table and in output filenames.
  if (rounded === 0) return hz.toExponential(3);
  return String(rounded);
}

/**
 * Renders a group of rates so that rates which differ read as differing.
 *
 * `formatRate` rounds to six decimals, which is what keeps an ordinary rate free of
 * float noise — 30 samples in a 0.1-second record is 299.99999999999994 as a double,
 * and belongs on screen as 300. Two rates separated by less than that round to one
 * string, so a file carrying 1e-6 Hz and 1.25e-6 Hz warned that it used "2 different
 * sampling rates (0.000001 Hz, 0.000001 Hz)" and named both files the same thing.
 *
 * That is the contradiction the exponent fallback above already removes for rates that
 * round away to zero; this is the same one a step further out. On a collision every rate
 * in the group switches to its shortest exact form, which is unique for distinct values,
 * rather than only the pair that collided — one column in one notation reads better than
 * two.
 */
export function formatRates(rates: readonly number[]): string[] {
  // `rates.map` on anything else is a TypeError naming a local of this package; the members
  // are `formatRate`'s business, one line down.
  if (!Array.isArray(rates)) {
    throw new OptionError(`rates must be a list of sampling rates, got ${describeValue(rates)}.`);
  }
  const rounded = rates.map(formatRate);
  const distinct = new Set(rates).size;
  return new Set(rounded).size === distinct ? rounded : rates.map((hz) => String(hz));
}
